/**
 * LES MENTIONS, COTE SOCLE D'INTERFACE : la convention de texte stocke et ce
 * qu'on en fait a l'ecran (contrat conversations du 05/10/2026, section 3).
 *
 *   - une personne : `@[personId]`   (jamais un nom : un nom change)
 *   - tout le monde : `@tous`
 *   - Lia : `@Lia`
 *
 * LES FONCTIONS PURES DE CE FICHIER SONT ECRITES DEUX FOIS, et c'est assume :
 * ici et dans `packages/data-model/src/mentions.ts` (le SDK derive `mentions`
 * du texte a l'envoi). `@taki/ui` ne depend d'aucun modele de donnees (voir
 * l'en-tete de `views/fields.ts`). Le test
 * `packages/ui/tests/conversations-mentions.test.mjs` compare les deux copies.
 *
 * Pour un editeur qui n'est PAS une textarea (un editeur riche) : la source
 * (`useSourceDeMentions`) et `filtrerMentions` alimentent sa liste, il insere
 * `entree.jeton` dans son document, et rend par `segmentsDeTexte` +
 * `libelleDeMention` (ou `TexteAvecMentions` sur un bloc texte).
 */

export const MENTION_TOUS = '@tous';
export const MENTION_LIA = '@Lia';

/** Une mention stockee : `@[personId]`, `@tous`, `@Lia`. Frontiere : pas de lettre ou de chiffre colle apres. */
export const MOTIF_MENTION_STOCKEE = /@\[([A-Za-z0-9_-]{1,64})\]|@(tous|lia)(?![\p{L}\p{N}_])/giu;

export interface Mentions {
  personnes: string[];
  tous: boolean;
  lia: boolean;
}

/** Les mentions d'un texte stocke : personIds sans doublon dans l'ordre d'apparition, `tous`, `lia`. */
export function mentionsDe(texte: string): Mentions {
  const personnes: string[] = [];
  let tous = false;
  let lia = false;
  for (const trouve of texte.matchAll(MOTIF_MENTION_STOCKEE)) {
    const personId = trouve[1];
    if (personId !== undefined) {
      if (!personnes.includes(personId)) personnes.push(personId);
    } else if (trouve[2]?.toLowerCase() === 'tous') {
      tous = true;
    } else {
      lia = true;
    }
  }
  return { personnes, tous, lia };
}

/** Une entree de la liste des mentions : une personne de l'org, `@tous`, ou Lia. */
export interface EntreeDeMention {
  /** Le personId, ou `tous`, ou `lia`. */
  id: string;
  genre: 'personne' | 'tous' | 'lia';
  /** Ce que la liste montre et ce que le texte affiche (« Celia », « tous », « Lia »). */
  libelle: string;
  /** L'adresse d'une personne, ou la phrase de l'entree speciale. */
  detail?: string;
  /** Ce qui est STOCKE : `@[p-celia]`, `@tous`, `@Lia`. */
  jeton: string;
  /** La photo de la personne (https), quand l'annuaire la connait : l'avatar des messages et de la liste. */
  photoURL?: string | null | undefined;
}

export const ENTREE_TOUS: EntreeDeMention = Object.freeze({
  id: 'tous', genre: 'tous', libelle: 'tous', detail: 'Toutes les personnes de la conversation', jeton: MENTION_TOUS,
}) as EntreeDeMention;

export const ENTREE_LIA: EntreeDeMention = Object.freeze({
  id: 'lia', genre: 'lia', libelle: 'Lia', detail: 'Lia repond une fois, avec vos droits', jeton: MENTION_LIA,
}) as EntreeDeMention;

/** Le jeton stocke d'une personne. */
export function jetonDePersonne(personId: string): string {
  return `@[${personId}]`;
}

/** Sans accent, sans casse, sans espace autour. */
export function normaliserPourMention(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('fr').trim();
}

/**
 * Les entrees dont le libelle, ou l'un de ses mots, commence par le prefixe
 * tape (sans accent ni casse). Prefixe vide : toutes. Au plus `max`.
 */
export function filtrerMentions(entrees: readonly EntreeDeMention[], prefixe: string, max = 8): EntreeDeMention[] {
  const voulu = normaliserPourMention(prefixe);
  if (voulu === '') return entrees.slice(0, max);
  // D'abord les libelles qui COMMENCENT par le prefixe (« @Ma » : Marc avant
  // Celia Martin), puis ceux dont un autre mot commence par lui ; l'ordre de la
  // source dans chaque groupe.
  const debut: EntreeDeMention[] = [];
  const mot: EntreeDeMention[] = [];
  for (const entree of entrees) {
    const libelle = normaliserPourMention(entree.libelle);
    if (libelle.startsWith(voulu)) debut.push(entree);
    else if (libelle.split(/[\s'-]+/u).some((m) => m.startsWith(voulu))) mot.push(entree);
  }
  return [...debut, ...mot].slice(0, max);
}

/** Un morceau d'un texte stocke : du texte, ou une mention. */
export type SegmentDeTexte =
  | { type: 'texte'; valeur: string }
  | { type: 'mention'; jeton: string; id: string; genre: EntreeDeMention['genre'] };

/** Decoupe un texte stocke en segments, pour le rendre en pastilles. */
export function segmentsDeTexte(texte: string): SegmentDeTexte[] {
  const segments: SegmentDeTexte[] = [];
  let curseur = 0;
  for (const trouve of texte.matchAll(MOTIF_MENTION_STOCKEE)) {
    const debut = trouve.index ?? 0;
    if (debut > curseur) segments.push({ type: 'texte', valeur: texte.slice(curseur, debut) });
    const personId = trouve[1];
    if (personId !== undefined) segments.push({ type: 'mention', jeton: trouve[0], id: personId, genre: 'personne' });
    else if (trouve[2]?.toLowerCase() === 'tous') segments.push({ type: 'mention', jeton: trouve[0], id: 'tous', genre: 'tous' });
    else segments.push({ type: 'mention', jeton: trouve[0], id: 'lia', genre: 'lia' });
    curseur = debut + trouve[0].length;
  }
  if (curseur < texte.length) segments.push({ type: 'texte', valeur: texte.slice(curseur) });
  return segments;
}

/** Le mot montre a la place d'un personId que l'annuaire ne connait pas (jamais l'identifiant). */
export const MENTION_INCONNUE = 'Compte inconnu';

/** Le libelle AFFICHE d'une mention : `@Celia`, `@tous`, `@Lia`, `@Compte inconnu`. */
export function libelleDeMention(segment: Extract<SegmentDeTexte, { type: 'mention' }>, entrees: readonly EntreeDeMention[]): string {
  if (segment.genre === 'tous') return MENTION_TOUS;
  if (segment.genre === 'lia') return MENTION_LIA;
  const entree = entrees.find((e) => e.genre === 'personne' && e.id === segment.id);
  return `@${entree?.libelle ?? MENTION_INCONNUE}`;
}

/** Le texte stocke rendu en texte lisible (noms a la place des jetons) : pour un extrait, un titre. */
export function texteAffiche(texte: string, entrees: readonly EntreeDeMention[]): string {
  return segmentsDeTexte(texte).map((s) => s.type === 'texte' ? s.valeur : libelleDeMention(s, entrees)).join('');
}

/**
 * CE QUE LA TEXTAREA MONTRE ET CE QU'ELLE STOCKE. Le champ affiche des noms
 * (`@Celia`) parce qu'un identifiant ne s'affiche jamais ; il stocke des
 * jetons (`@[p-celia]`). Les deux fonctions font l'aller et le retour, a
 * partir de la source : un nom qui n'est pas dans la source reste du texte.
 */
export function versAffichage(texteStocke: string, entrees: readonly EntreeDeMention[]): string {
  return texteAffiche(texteStocke, entrees);
}

/** Les libelles AFFICHES, du plus long au plus court, pour ne pas couper « Marc-Antoine » en « Marc ». */
function motifDesLibelles(entrees: readonly EntreeDeMention[]): RegExp | null {
  const libelles = [...new Set(entrees.map((e) => e.libelle).filter((l) => l.length > 0))]
    .sort((a, b) => b.length - a.length)
    .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (libelles.length === 0) return null;
  return new RegExp(`@(${libelles.join('|')})(?![\\p{L}\\p{N}_])`, 'gu');
}

/** L'inverse : le texte affiche (`@Celia`) rendu en texte stocke (`@[p-celia]`), par la source. */
export function versStockage(texteAffiche: string, entrees: readonly EntreeDeMention[]): string {
  const motif = motifDesLibelles(entrees);
  if (motif === null) return texteAffiche;
  return texteAffiche.replace(motif, (tout, libelle: string) => {
    const entree = entrees.find((e) => e.libelle === libelle);
    return entree === undefined ? tout : entree.jeton;
  });
}

/**
 * LES MENTIONS DEJA CHOISIES dans le texte AFFICHE (`@Célia Dupont`) : leurs
 * bornes, pour les dessiner en pastille dans la zone de saisie et les effacer
 * d'un coup au Retour arriere. Seuls les libelles de la source comptent.
 */
export function mentionsAffichees(texteAffiche: string, entrees: readonly EntreeDeMention[]): Array<{ debut: number; fin: number; entree: EntreeDeMention }> {
  const motif = motifDesLibelles(entrees);
  if (motif === null) return [];
  const bornes: Array<{ debut: number; fin: number; entree: EntreeDeMention }> = [];
  for (const trouve of texteAffiche.matchAll(motif)) {
    const debut = trouve.index ?? 0;
    // Un `@` colle a une lettre (une adresse mail) n'est pas une mention.
    if (debut > 0 && /[\p{L}\p{N}_]/u.test(texteAffiche[debut - 1] ?? '')) continue;
    const entree = entrees.find((e) => e.libelle === trouve[1]);
    if (entree !== undefined) bornes.push({ debut, fin: debut + trouve[0].length, entree });
  }
  return bornes;
}

/**
 * La mention en cours de frappe : le prefixe apres le dernier `@` avant le
 * curseur, ou `null`. Avec `entrees`, une mention DEJA CHOISIE (le libelle
 * complet, suivi d'un espace) n'est plus « en cours » : la liste se ferme des
 * qu'une personne est choisie et ne se rouvre qu'a un nouveau `@` (R2).
 */
export function mentionEnCours(texte: string, curseur: number, entrees?: readonly EntreeDeMention[]): { debut: number; prefixe: string } | null {
  const avant = texte.slice(0, curseur);
  const arobase = avant.lastIndexOf('@');
  if (arobase < 0) return null;
  if (entrees !== undefined && mentionsAffichees(texte, entrees).some((m) => m.debut === arobase && m.fin < curseur)) return null;
  // Un `@` colle a une lettre (une adresse mail) n'ouvre pas la liste.
  if (arobase > 0 && /[\p{L}\p{N}_]/u.test(avant[arobase - 1] ?? '')) return null;
  const prefixe = avant.slice(arobase + 1);
  if (/[\n@]/.test(prefixe) || prefixe.length > 40) return null;
  return { debut: arobase, prefixe };
}

/** Insere l'entree choisie a la place de la mention en cours ; rend le texte et la position du curseur. */
export function insererMention(texte: string, curseur: number, entree: EntreeDeMention): { texte: string; curseur: number } {
  const enCours = mentionEnCours(texte, curseur);
  const debut = enCours?.debut ?? curseur;
  const insertion = `@${entree.libelle} `;
  const resultat = `${texte.slice(0, debut)}${insertion}${texte.slice(curseur)}`;
  return { texte: resultat, curseur: debut + insertion.length };
}

/**
 * LES ENTREES QUE PROPOSE LE COMPOSEUR d'une conversation (V3-5 du
 * 07/10/2026) : dans un message DIRECT, ni les personnes ni `@tous` (on y est
 * deja entre soi), seulement `@Lia`, qui peut y repondre ; ailleurs, toutes.
 * Le FIL garde toutes les entrees pour nommer les auteurs et les mentions
 * deja ecrites.
 */
export function entreesDuComposeur<T extends readonly EntreeDeMention[]>(entrees: T, typeDeConversation: string | undefined): T {
  return typeDeConversation === 'direct' ? (entrees.filter((e) => e.genre === 'lia') as unknown as T) : entrees;
}
